import { forwardRef, useImperativeHandle, useRef, useState, useEffect } from 'react';
import { collection, addDoc, getDocs, deleteDoc, doc, query, where } from 'firebase/firestore';
import { db } from '../firebase';

import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

import {
  FiUsers,
  FiAlertTriangle,
  FiActivity,
  FiRadio
} from 'react-icons/fi';

import { brand } from '../utils/brand';
import vitaIcon from '../assets/vita-icon.png';
import MapSection from './MapSection';
import MetricCard from './MetricCard';
import TerritorialIntelligencePanel from './TerritorialIntelligencePanel';
import { buildTerritorialOverview } from '../utils/buildTerritorialOverview';
import KnowledgeGraph from './KnowledgeGraph';
import { useCognitiveAlerts } from '../hooks/useCognitiveAlerts';

const ExecutiveReport = forwardRef(function ExecutiveReport(
  {
    activeProject,
    projectActors = [],
    processedActors = [],
    projectAlerts = [],
    projectTimeline = [],
    projectSignals = [],
    projectPrediction = null,
    activeProjectId = null,
    informeExternoTexto = '',
    territorialFilters = {
      selectedRegion: '',
      selectedComuna: '',
      selectedSector: '',
      selectedFoco: ''
    }
  },
  ref
) {
  const mapRef = useRef(null);
  const graphRef = useRef(null);
  const [interpretacionAnalista, setInterpretacionAnalista] = useState('');
  const [informesGuardados, setInformesGuardados] = useState([]);
  const [tituloInforme, setTituloInforme] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [informeSeleccionadoId, setInformeSeleccionadoId] = useState(null);

  // ── Cargar informes guardados ─────────────────────────
  useEffect(() => {
    if (!activeProjectId) return;
    const cargar = async () => {
      try {
        const q = query(
          collection(db, 'informes'),
          where('projectId', '==', activeProjectId)
        );
        const snap = await getDocs(q);
        const data = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
        setInformesGuardados(data);
      } catch (e) {
        console.error('Error cargando informes:', e);
      }
    };
    cargar();
  }, [activeProjectId]);

  const guardarInforme = async () => {
    if (!interpretacionAnalista.trim()) return;
    if (!activeProjectId) return;
    setGuardando(true);
    try {
      const titulo = tituloInforme.trim() ||
        `Informe ${new Date().toLocaleDateString('es-CL')}`;
      const docRef = await addDoc(collection(db, 'informes'), {
        projectId: activeProjectId,
        titulo,
        texto: interpretacionAnalista.trim(),
        fecha: new Date().toISOString()
      });
      setInformesGuardados((prev) => [{
        id: docRef.id,
        projectId: activeProjectId,
        titulo,
        texto: interpretacionAnalista.trim(),
        fecha: new Date().toISOString()
      }, ...prev]);
      setInformeSeleccionadoId(docRef.id);
      setTituloInforme('');
    } catch (e) {
      console.error('Error guardando informe:', e);
    }
    setGuardando(false);
  };

  const cargarInforme = (informe) => {
    setInterpretacionAnalista(informe.texto);
    setInformeSeleccionadoId(informe.id);
  };

  const eliminarInforme = async (e, informeId) => {
    e.stopPropagation();
    try {
      await deleteDoc(doc(db, 'informes', informeId));
      setInformesGuardados((prev) => prev.filter((i) => i.id !== informeId));
      if (informeSeleccionadoId === informeId) {
        setInterpretacionAnalista('');
        setInformeSeleccionadoId(null);
      }
    } catch (err) {
      console.error('Error eliminando informe:', err);
    }
  };

  // ── Filtros ──────────────────────────────────────────────
  const filteredActors = projectActors.filter((actor) =>
    itemMatchesTerritory(actor, territorialFilters)
  );

  const filteredProcessedActors = processedActors.filter((actor) =>
    itemMatchesTerritory(actor, territorialFilters)
  );

  const filteredAlerts = projectAlerts.filter((alerta) =>
    itemMatchesTerritoryFlexible(alerta, territorialFilters, filteredActors)
  );

  const filteredTimeline = projectTimeline.filter((evento) =>
    itemMatchesTerritoryFlexible(evento, territorialFilters, filteredActors)
  );

  const filteredSignals = projectSignals.filter((signal) =>
    itemMatchesTerritoryFlexible(signal, territorialFilters, filteredActors)
  );

  const alertasActivas = filteredAlerts.filter(
    (alerta) => alerta.estado !== 'Cerrada'
  );

  const actoresCriticos = filteredActors.filter(
    (actor) =>
      actor.estado === 'Crítico' ||
      actor.riesgo === 'Crítico' ||
      actor.riesgo === 'Alto'
  );

  // ── Motor cognitivo real ─────────────────────────────────
  const overview = buildTerritorialOverview({
    actors: filteredProcessedActors,
    alerts: filteredAlerts,
    signals: filteredSignals,
    timeline: filteredTimeline,
    filters: {}
  });

  const territorioLabel = [
    territorialFilters.selectedSector,
    territorialFilters.selectedComuna,
    territorialFilters.selectedRegion
  ]
    .filter(Boolean)
    .join(' · ');

  // ── Actores críticos procesados (con IVT) ────────────────
  const actoresCriticosConEngine = filteredProcessedActors
    .filter((a) => {
      const estado = a._vita?.state?.state;
      return ['Escalamiento Territorial', 'Convergencia Narrativa',
        'Activación Territorial', 'Sensibilidad Creciente'].includes(estado);
    })
    .sort((a, b) => (b._vita?.scoring?.IVT || 0) - (a._vita?.scoring?.IVT || 0));
  const cognitiveAlerts = useCognitiveAlerts(filteredProcessedActors);

  // ── Narrativas activas ───────────────────────────────────
  const narrativasActivas = [
    overview.narrativaDominante,
    ...(overview.ecosistemaNarrativo?.secundarias || [])
  ].filter(Boolean).slice(0, 5);

  // ── Helpers PDF ──────────────────────────────────────────
  const cargarImagen = (src) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = src;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
    });
  };

  const addHeader = (pdf, logoData, title) => {
    pdf.setFillColor(7, 26, 47);
    pdf.rect(0, 0, 210, 28, 'F');
    pdf.addImage(logoData, 'PNG', 14, 6, 12, 12);
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(16);
    pdf.text(title, 32, 16);
  };

  const addFooter = (pdf, pageNumber) => {
    const pageHeight = pdf.internal.pageSize.height;
    pdf.setDrawColor(220, 225, 230);
    pdf.line(14, pageHeight - 14, 196, pageHeight - 14);
    pdf.setFontSize(8);
    pdf.setTextColor(120, 130, 145);
    pdf.text('VITA — Territorial Intelligence Platform', 14, pageHeight - 8);
    pdf.text('Confidencial', 105, pageHeight - 8, { align: 'center' });
    const fecha = new Date().toLocaleDateString('es-CL');
    pdf.text(fecha, 165, pageHeight - 8);
    pdf.text(`Página ${pageNumber}`, 196, pageHeight - 8, { align: 'right' });
  };

  // ── Export PDF ───────────────────────────────────────────
  const exportarPDF = async () => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const logoData = await cargarImagen(vitaIcon);
    const currentPageRef = { value: 1 };

    const addPageWithHeader = (title) => {
      addFooter(pdf, currentPageRef.value);
      pdf.addPage();
      currentPageRef.value += 1;
      addHeader(pdf, logoData, title);
    };

    const checkPageBreak = (currentY, title, resetY = 44) => {
      if (currentY > 255) {
        addPageWithHeader(title);
        return resetY;
      }
      return currentY;
    };

    const drawStakeholderHeader = (tableY) => {
      pdf.setFillColor(7, 26, 47);
      pdf.roundedRect(20, tableY - 6, 170, 10, 2, 2, 'F');
      pdf.setFontSize(9);
      pdf.setTextColor(255, 255, 255);
      pdf.text('Actor', 24, tableY);
      pdf.text('Estado Territorial', 72, tableY);
      pdf.text('IVT', 130, tableY);
      pdf.text('Influencia', 155, tableY);
      return tableY + 12;
    };

    // ── PÁGINA 1: PORTADA ─────────────────────────────────
    pdf.setFillColor(7, 26, 47);
    pdf.rect(0, 0, 210, 46, 'F');
    pdf.addImage(logoData, 'PNG', 16, 10, 16, 16);
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(24);
    pdf.text('VITA', 36, 20);
    pdf.setFontSize(10);
    pdf.text('Territorial Intelligence', 36, 27);
    pdf.setFillColor(31, 169, 122);
    pdf.rect(0, 44, 210, 2, 'F');

    let y = 65;

    pdf.setTextColor(7, 26, 47);
    pdf.setFontSize(24);
    pdf.text('Reporte Territorial Ejecutivo', 20, y);

    y += 12;
    pdf.setFontSize(12);
    pdf.setTextColor(90, 100, 115);
    pdf.text(activeProject?.nombre || 'Proyecto sin nombre', 20, y);

    y += 8;
    pdf.text(
      `Cliente: ${activeProject?.cliente || '-'}   Región: ${activeProject?.region || '-'}`,
      20, y
    );

    y += 8;
    pdf.text(
      `Territorio analizado: ${territorioLabel || 'Vista general del proyecto'}`,
      20, y
    );

    y += 18;

    // KPIs
    const kpis = [
      { label: 'Stakeholders', value: filteredActors.length, color: [9, 35, 149] },
      { label: 'Críticos', value: actoresCriticos.length, color: [220, 38, 38] },
      { label: 'Alertas', value: alertasActivas.length, color: [245, 158, 11] },
      { label: 'RRSS', value: filteredSignals.length, color: [31, 169, 122] }
    ];

    let x = 20;
    kpis.forEach((kpi) => {
      pdf.setFillColor(248, 250, 252);
      pdf.roundedRect(x, y, 38, 30, 4, 4, 'F');
      pdf.setDrawColor(230, 235, 240);
      pdf.roundedRect(x, y, 38, 30, 4, 4);
      pdf.setFillColor(...kpi.color);
      pdf.circle(x + 8, y + 8, 3, 'F');
      pdf.setFontSize(16);
      pdf.setTextColor(7, 26, 47);
      pdf.text(String(kpi.value), x + 6, y + 18);
      pdf.setFontSize(8);
      pdf.setTextColor(90, 100, 115);
      pdf.text(kpi.label, x + 6, y + 25);
      x += 43;
    });

    y += 42;

    // ── ESTADO TERRITORIAL COGNITIVO ──────────────────────
    const estadoColor = {
      'Escalamiento Territorial': [220, 38, 38],
      'Convergencia Narrativa': [234, 88, 12],
      'Activación Territorial': [202, 138, 4],
      'Sensibilidad Creciente': [22, 163, 74],
      'Observación': [6, 182, 212],
      'Estable': [100, 116, 139]
    }[overview.estadoTerritorial] || [100, 116, 139];

    pdf.setFillColor(248, 250, 252);
    pdf.roundedRect(20, y, 170, 32, 4, 4, 'F');
    pdf.setDrawColor(...estadoColor);
    pdf.setLineWidth(0.8);
    pdf.roundedRect(20, y, 4, 32, 2, 2, 'F');
    pdf.setLineWidth(0.2);

    pdf.setFontSize(8);
    pdf.setTextColor(90, 100, 115);
    pdf.text('ESTADO TERRITORIAL', 28, y + 8);

    pdf.setFontSize(14);
    pdf.setTextColor(...estadoColor);
    pdf.text(overview.estadoTerritorial || 'Estable', 28, y + 17);

    pdf.setFontSize(9);
    pdf.setTextColor(50, 60, 75);
    const descLineas = pdf.splitTextToSize(overview.descripcionEstado || '', 135);
    pdf.text(descLineas, 28, y + 25);

    y += 40;

    // ── NARRATIVA DOMINANTE ───────────────────────────────
    if (overview.narrativaDominante && overview.narrativaDominante !== 'Sin narrativa territorial detectada') {
      pdf.setFontSize(8);
      pdf.setTextColor(9, 147, 181);
      pdf.text('NARRATIVA DOMINANTE', 20, y);

      y += 6;
      pdf.setFontSize(11);
      pdf.setTextColor(7, 26, 47);
      const narrativaLineas = pdf.splitTextToSize(`"${overview.narrativaDominante}"`, 170);
      pdf.text(narrativaLineas, 20, y);
      y += narrativaLineas.length * 5 + 8;
    }

    // ── RELATO TERRITORIAL ────────────────────────────────
    if (overview.ecosistemaNarrativo?.relato) {
      pdf.setFontSize(8);
      pdf.setTextColor(9, 147, 181);
      pdf.text('RELATO TERRITORIAL', 20, y);

      y += 6;
      pdf.setFillColor(240, 249, 255);
      const relatoLineas = pdf.splitTextToSize(overview.ecosistemaNarrativo.relato, 158);
      const relatoH = relatoLineas.length * 5.5 + 14;
      pdf.roundedRect(20, y, 170, relatoH, 3, 3, 'F');
      pdf.setDrawColor(9, 147, 181);
      pdf.setLineWidth(0.6);
      pdf.roundedRect(20, y, 3, relatoH, 1, 1, 'F');
      pdf.setLineWidth(0.2);
      pdf.setFontSize(9);
      pdf.setTextColor(7, 26, 47);
      pdf.text(relatoLineas, 26, y + 8);
      y += relatoH + 14;
    }

    // ── ACCIÓN SUGERIDA ───────────────────────────────────
    pdf.setFontSize(8);
    pdf.setTextColor(31, 169, 122);
    pdf.text('ACCIÓN SUGERIDA', 20, y);

    y += 6;
    pdf.setFontSize(10);
    pdf.setTextColor(50, 60, 75);
    const accionLineas = pdf.splitTextToSize(overview.accionSugerida || '', 170);
    pdf.text(accionLineas, 20, y);
    y += accionLineas.length * 5 + 10;
    
    y = checkPageBreak(y, 'Reporte Territorial Ejecutivo', 44);

    // ── MÉTRICAS RÁPIDAS ──────────────────────────────────
    if (y > 240) {
      addFooter(pdf, currentPageRef.value);
      pdf.addPage();
      currentPageRef.value += 1;
      addHeader(pdf, logoData, 'Reporte Territorial Ejecutivo');
      y = 44;
    }

    const metricasRapidas = [
      { label: 'IVT promedio', value: `${Math.round(filteredProcessedActors.reduce((s, a) => s + (a._vita?.scoring?.IVT || 0), 0) / (filteredProcessedActors.length || 1))}%` },
      { label: 'Propagacion', value: overview.propagacion || '-' },
      { label: 'Tendencia', value: overview.tendencia || '-' },
      { label: 'Riesgo', value: `${overview.riesgoGeneral || 0}%` }
    ];

    let mx = 20;
    metricasRapidas.forEach((m) => {
      pdf.setFillColor(248, 250, 252);
      pdf.roundedRect(mx, y, 38, 24, 3, 3, 'F');
      pdf.setDrawColor(226, 232, 240);
      pdf.roundedRect(mx, y, 38, 24, 3, 3);
      pdf.setFontSize(7);
      pdf.setTextColor(90, 100, 115);
      pdf.text(m.label, mx + 4, y + 9);
      pdf.setFontSize(13);
      pdf.setTextColor(7, 26, 47);
      pdf.text(String(m.value), mx + 4, y + 19);
      mx += 43;
    });

    let analY = y + 36;

    // ── Si no cabe en la misma página, nueva página ───────
    if (analY > 230) {
      addFooter(pdf, currentPageRef.value);
      pdf.addPage();
      currentPageRef.value += 1;
      addHeader(pdf, logoData, 'Interpretación del Analista');
      analY = 44;
    }

    pdf.setFillColor(7, 26, 47);
    pdf.roundedRect(20, analY - 6, 170, 12, 3, 3, 'F');
    pdf.setFontSize(13);
    pdf.setTextColor(255, 255, 255);
    pdf.text('LECTURA DEL ANALISTA TERRITORIAL', 28, analY + 2);

    analY += 18;

    const textoFinal = informeExternoTexto.trim() || interpretacionAnalista.trim();
    if (textoFinal) {
      pdf.setFillColor(248, 250, 252);
      const textoAnalista = pdf.splitTextToSize(textoFinal, 155);
      const alturaBloque = textoAnalista.length * 5.5 + 16;
      pdf.roundedRect(20, analY, 170, alturaBloque, 4, 4, 'F');
      pdf.setDrawColor(31, 169, 122);
      pdf.setLineWidth(0.8);
      pdf.roundedRect(20, analY, 4, alturaBloque, 2, 2, 'F');
      pdf.setLineWidth(0.2);
      pdf.setFontSize(9.5);
      pdf.setTextColor(50, 60, 75);
      pdf.text(textoAnalista, 28, analY + 9);
      analY += alturaBloque + 10;
    } else {
      pdf.setFillColor(248, 250, 252);
      pdf.roundedRect(20, analY, 170, 16, 4, 4, 'F');
      pdf.setFontSize(10);
      pdf.setTextColor(150, 160, 175);
      pdf.text('Sin interpretación registrada por el analista.', 28, analY + 11);
      analY += 22;
    }

    // ── NARRATIVAS ACTIVAS ────────────────────────────────
    if (narrativasActivas.length > 0) {
      addFooter(pdf, currentPageRef.value);
      pdf.addPage();
      currentPageRef.value += 1;
      addHeader(pdf, logoData, 'Interpretación del Analista');
      analY = 44;

      pdf.setFontSize(13);
      pdf.setTextColor(7, 26, 47);
      pdf.text('Narrativas territoriales activas', 20, analY);

      analY += 10;

      narrativasActivas.forEach((narrativa, i) => {
        analY = checkPageBreak(analY, 'Interpretación del Analista');

        pdf.setFillColor(i === 0 ? 6 : 248, i === 0 ? 182 : 250, i === 0 ? 212 : 252);
        pdf.roundedRect(20, analY - 4, 170, 12, 3, 3, 'F');
        pdf.setFontSize(10);
        pdf.setTextColor(i === 0 ? 7 : 50, i === 0 ? 26 : 60, i === 0 ? 47 : 75);
        pdf.setFillColor(i === 0 ? 9 : 150, i === 0 ? 147 : 160, i === 0 ? 181 : 175);
        pdf.circle(24, analY + 2, 2, 'F');
        pdf.setFontSize(10);
        pdf.setTextColor(i === 0 ? 7 : 50, i === 0 ? 26 : 60, i === 0 ? 47 : 75);
        pdf.text(String(narrativa).slice(0, 74), 28, analY + 4);
        analY += 16;
      });

      analY += 8;
    }

    // ── ACTORES CRÍTICOS CON ENGINE ───────────────────────
    if (actoresCriticosConEngine.length > 0) {
      analY = checkPageBreak(analY, 'Interpretación del Analista');

      pdf.setFontSize(13);
      pdf.setTextColor(7, 26, 47);
      pdf.text('Actores territoriales en estado crítico', 20, analY);

      analY += 10;
      analY = drawStakeholderHeader(analY);

      actoresCriticosConEngine.forEach((actor, index) => {
        analY = checkPageBreak(analY, 'Interpretación del Analista');

        const rowBg = index % 2 === 0 ? [248, 250, 252] : [255, 255, 255];
        pdf.setFillColor(...rowBg);
        pdf.roundedRect(20, analY - 5, 170, 12, 1, 1, 'F');

        const estado = actor._vita?.state?.state || '-';
        const ivt = actor._vita?.scoring?.IVT ?? '-';

        pdf.setFontSize(9);
        pdf.setTextColor(7, 26, 47);
        pdf.text(String(actor.nombre || '-').slice(0, 28), 24, analY + 2);

        pdf.setTextColor(50, 60, 75);
        pdf.text(String(estado).slice(0, 24), 76, analY + 2);
        pdf.text(ivt !== '-' ? `${ivt}%` : '-', 130, analY + 2);
        pdf.text(String(actor.influencia || '-'), 155, analY + 2);

        if (actor._vita?.reasoning?.narrativaDominante) {
          analY += 10;
          pdf.setFontSize(8);
          pdf.setTextColor(100, 116, 139);
          pdf.text(
            `  "${String(actor._vita.reasoning.narrativaDominante).slice(0, 70)}"`,
            24, analY + 2
          );
        }

        analY += 14;
      });
    }

    addFooter(pdf, currentPageRef.value);

    // ── PÁGINA 3: RADAR PREDICTIVO ────────────────────────
    addPageWithHeader('Radar Territorial Ejecutivo');

    let radarY = 44;

    pdf.setTextColor(7, 26, 47);
    pdf.setFontSize(20);
    pdf.text('Lectura cognitiva territorial', 20, radarY);

    radarY += 14;

    const metricasRadar = [
      { label: 'Estado territorial', value: overview.estadoTerritorial || 'Estable' },
      { label: 'Propagación', value: overview.propagacion || '-' },
      { label: 'Tendencia', value: overview.tendencia || '-' },
      { label: 'Riesgo general', value: `${overview.riesgoGeneral || 0}%` }
    ];

    metricasRadar.forEach((m, i) => {
      const colX = i < 2 ? 20 : 105;
      const rowY = radarY + (i % 2) * 30;

      pdf.setFillColor(248, 250, 252);
      pdf.roundedRect(colX, rowY, 80, 24, 4, 4, 'F');
      pdf.setDrawColor(226, 232, 240);
      pdf.roundedRect(colX, rowY, 80, 24, 4, 4);

      pdf.setFontSize(8);
      pdf.setTextColor(90, 100, 115);
      pdf.text(m.label, colX + 5, rowY + 9);

      pdf.setFontSize(13);
      pdf.setTextColor(7, 26, 47);
      pdf.text(String(m.value).slice(0, 22), colX + 5, rowY + 19);
    });

    radarY += 74;

    if (projectPrediction && !projectPrediction.insuficiente) {
      pdf.setFontSize(13);
      pdf.setTextColor(7, 26, 47);
      pdf.text('Motor predictivo territorial', 20, radarY);

      radarY += 10;

      pdf.setFillColor(248, 250, 252);
      pdf.roundedRect(20, radarY, 170, 28, 4, 4, 'F');
      pdf.setDrawColor(226, 232, 240);
      pdf.roundedRect(20, radarY, 170, 28, 4, 4);

      pdf.setFontSize(9);
      pdf.setTextColor(90, 100, 115);
      pdf.text(
        `Tendencia general: ${projectPrediction.tendenciaGeneral}   ·   IVT proyectado 30d: ${projectPrediction.IVTProyectadoPromedio}%   ·   Actores con historial: ${projectPrediction.totalActoresConHistorial}`,
        26, radarY + 10
      );

      if (projectPrediction.accionPrioritaria) {
        pdf.setFontSize(9);
        pdf.setTextColor(50, 60, 75);
        const predLineas = pdf.splitTextToSize(projectPrediction.accionPrioritaria, 155);
        pdf.text(predLineas, 26, radarY + 20);
      }

      radarY += 42;

      if (projectPrediction.actoresEnEscalamiento?.length > 0) {
        pdf.setFontSize(11);
        pdf.setTextColor(220, 38, 38);
        pdf.text('(!) Actores proyectando escalamiento', 20, radarY);
        radarY += 8;

        projectPrediction.actoresEnEscalamiento.forEach((actor) => {
          radarY = checkPageBreak(radarY, 'Radar Territorial Ejecutivo');
          pdf.setFontSize(9);
          pdf.setTextColor(7, 26, 47);
          pdf.text(
            `${actor.nombre}   IVT actual ${actor.IVTActual}% -> proyectado ${actor.IVTProyectado30}% en 30d`,
            24, radarY
          );
          radarY += 8;
        });
      }
    } else {
      pdf.setFontSize(10);
      pdf.setTextColor(150, 160, 175);
      pdf.text('Motor predictivo en acumulación de historial. Edita y guarda stakeholders para activarlo.', 20, radarY);
    }

    // ── PÁGINA 4: MAPA ────────────────────────────────────
    addPageWithHeader('Mapa Territorial Ejecutivo');

    pdf.setTextColor(7, 26, 47);
    pdf.setFontSize(20);
    pdf.text('Lectura territorial georreferenciada', 20, 44);
    pdf.setFontSize(10);
    pdf.setTextColor(90, 100, 115);
    pdf.text(territorioLabel || 'Vista territorial general del proyecto', 20, 52);

    if (mapRef.current) {
      await new Promise((resolve) => setTimeout(resolve, 600));

      const canvas = await html2canvas(mapRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/png');
      const imgWidth = 194;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(imgData, 'PNG', 8, 58, imgWidth, imgHeight);

      let actorsY = 58 + imgHeight + 14;

      pdf.setFontSize(13);
      pdf.setTextColor(7, 26, 47);
      pdf.text('Actores relevantes del territorio', 20, actorsY);
      actorsY += 8;

      filteredActors.slice(0, 4).forEach((actor, index) => {
        actorsY = checkPageBreak(actorsY, 'Mapa Territorial Ejecutivo');

        pdf.setFillColor(index % 2 === 0 ? 248 : 255, index % 2 === 0 ? 250 : 255, index % 2 === 0 ? 252 : 255);
        pdf.roundedRect(20, actorsY, 170, 13, 2, 2, 'F');
        pdf.setDrawColor(226, 232, 240);
        pdf.roundedRect(20, actorsY, 170, 13, 2, 2);

        pdf.setFontSize(9);
        pdf.setTextColor(7, 26, 47);
        pdf.text(String(actor.nombre || '-').slice(0, 28), 25, actorsY + 5);

        pdf.setFontSize(8);
        pdf.setTextColor(90, 100, 115);
        const rol = actor.tipo || actor.cargo || 'Stakeholder';
        const ubi = actor.sector || actor.comuna || 'Territorio';
        pdf.text(`${rol} · ${ubi}`.slice(0, 50), 25, actorsY + 10);

        if (actor.riesgo) {
          pdf.setFillColor(220, 38, 38);
          pdf.roundedRect(155, actorsY + 3, 30, 6, 2, 2, 'F');
          pdf.setFontSize(7);
          pdf.setTextColor(255, 255, 255);
          pdf.text(`Riesgo ${actor.riesgo}`.slice(0, 16), 158, actorsY + 7);
        }

        actorsY += 16;
      });
    }

    // ── KNOWLEDGE GRAPH ───────────────────────────────────
    if (graphRef.current) {
      addPageWithHeader('Knowledge Graph Territorial');

      pdf.setTextColor(7, 26, 47);
      pdf.setFontSize(20);
      pdf.text('Red relacional cognitiva territorial', 20, 44);

      pdf.setFontSize(10);
      pdf.setTextColor(90, 100, 115);
      pdf.text('Conexiones detectadas desde convergencia narrativa entre actores.', 20, 52);

      await new Promise((resolve) => setTimeout(resolve, 1200));

      try {
        const graphCanvas = await html2canvas(graphRef.current, {
          scale: 1.5,
          useCORS: true,
          allowTaint: false,
          backgroundColor: '#071a2f'
        });

        const graphData = graphCanvas.toDataURL('image/png');
        const graphWidth = 190;
        const graphHeight = (graphCanvas.height * graphWidth) / graphCanvas.width;

        pdf.addImage(graphData, 'PNG', 10, 58, graphWidth, Math.min(graphHeight, 180));
      } catch (e) {
        pdf.setFontSize(10);
        pdf.setTextColor(150, 160, 175);
        pdf.text('Knowledge Graph no disponible en esta exportacion.', 20, 80);
      }
    }

    // ── PÁGINA 5: STAKEHOLDERS CRÍTICOS ───────────────────
    addPageWithHeader('Stakeholders Críticos');

    let tableY = 44;

    pdf.setTextColor(7, 26, 47);
    pdf.setFontSize(18);
    pdf.text('Actores prioritarios del territorio', 20, tableY);

    tableY += 14;
    tableY = drawStakeholderHeader(tableY);

    const actoresParaTabla = actoresCriticosConEngine.length > 0
      ? actoresCriticosConEngine
      : actoresCriticos;

    actoresParaTabla.forEach((actor, index) => {
      tableY = checkPageBreak(tableY, 'Stakeholders Críticos');

      if (tableY === 44) {
        pdf.setTextColor(7, 26, 47);
        pdf.setFontSize(18);
        pdf.text('Actores prioritarios del territorio', 20, tableY);
        tableY += 14;
        tableY = drawStakeholderHeader(tableY);
      }

      const rowBg = index % 2 === 0 ? [248, 250, 252] : [255, 255, 255];
      pdf.setFillColor(...rowBg);
      pdf.roundedRect(20, tableY - 5, 170, 10, 1, 1, 'F');

      const estadoActor = actor._vita?.state?.state || actor.estado || '-';
      const ivtActor = actor._vita?.scoring?.IVT;

      pdf.setFontSize(9);
      pdf.setTextColor(7, 26, 47);
      pdf.text(String(actor.nombre || '-').slice(0, 28), 24, tableY + 1);
      pdf.setTextColor(50, 60, 75);
      pdf.text(String(estadoActor).slice(0, 24), 76, tableY + 1);
      pdf.text(ivtActor !== undefined ? `${ivtActor}%` : '-', 130, tableY + 1);
      pdf.text(String(actor.influencia || '-'), 155, tableY + 1);

      tableY += 12;
    });

    // ── PÁGINA 6: ALERTAS, RRSS Y TIMELINE ───────────────
    addPageWithHeader('Alertas, RRSS y Timeline');

    let finalY = 44;

    pdf.setFontSize(16);
    pdf.setTextColor(7, 26, 47);
    pdf.text('Alertas territoriales relevantes', 20, finalY);
    finalY += 12;

    if (alertasActivas.length === 0) {
      pdf.setFontSize(10);
      pdf.setTextColor(150, 160, 175);
      pdf.text('Sin alertas activas registradas.', 24, finalY);
      finalY += 12;
    }

    alertasActivas.forEach((alerta) => {
      finalY = checkPageBreak(finalY, 'Alertas, RRSS y Timeline');

      let alertColor = [245, 158, 11];
      if (normalize(alerta.nivel).includes('critica') || normalize(alerta.nivel).includes('crítica')) {
        alertColor = [220, 38, 38];
      }

      pdf.setFillColor(248, 250, 252);
      pdf.roundedRect(20, finalY, 170, 16, 2, 2, 'F');
      pdf.setDrawColor(226, 232, 240);
      pdf.roundedRect(20, finalY, 170, 16, 2, 2);
      pdf.setFillColor(...alertColor);
      pdf.circle(28, finalY + 8, 2.5, 'F');
      pdf.setFontSize(10);
      pdf.setTextColor(7, 26, 47);
      pdf.text(String(alerta.titulo || 'Alerta territorial').slice(0, 70), 34, finalY + 7);
      pdf.setFontSize(8);
      pdf.setTextColor(90, 100, 115);
      pdf.text(`Nivel: ${alerta.nivel || '-'} · Actor: ${alerta.actor || '-'}`, 34, finalY + 12);
      finalY += 20;
    });

    finalY += 8;
    finalY = checkPageBreak(finalY, 'Alertas, RRSS y Timeline');

    pdf.setFontSize(16);
    pdf.setTextColor(7, 26, 47);
    pdf.text('Señales sociales y RRSS', 20, finalY);
    finalY += 12;

    if (filteredSignals.length === 0) {
      pdf.setFontSize(10);
      pdf.setTextColor(150, 160, 175);
      pdf.text('Sin señales RRSS registradas.', 24, finalY);
      finalY += 12;
    }

    filteredSignals.forEach((signal) => {
      finalY = checkPageBreak(finalY, 'Alertas, RRSS y Timeline');

      let signalColor = [6, 182, 212];
      if (normalize(signal.sentimiento).includes('negativo') || normalize(signal.riesgo).includes('alto') || normalize(signal.riesgo).includes('critico')) {
        signalColor = [220, 38, 38];
      }

      pdf.setFillColor(248, 250, 252);
      pdf.roundedRect(20, finalY, 170, 16, 2, 2, 'F');
      pdf.setDrawColor(226, 232, 240);
      pdf.roundedRect(20, finalY, 170, 16, 2, 2);
      pdf.setFillColor(...signalColor);
      pdf.circle(28, finalY + 8, 2.5, 'F');
      pdf.setFontSize(10);
      pdf.setTextColor(7, 26, 47);
      pdf.text(String(signal.red || 'RRSS').slice(0, 70), 34, finalY + 7);
      pdf.setFontSize(8);
      pdf.setTextColor(90, 100, 115);
      pdf.text(`Sentimiento: ${signal.sentimiento || '-'} · Riesgo: ${signal.riesgo || '-'}`, 34, finalY + 12);
      finalY += 20;
    });

    finalY += 8;
    finalY = checkPageBreak(finalY, 'Alertas, RRSS y Timeline');

    pdf.setFontSize(16);
    pdf.setTextColor(7, 26, 47);
    pdf.text('Timeline territorial', 20, finalY);
    finalY += 14;

    if (filteredTimeline.length === 0) {
      pdf.setFontSize(10);
      pdf.setTextColor(150, 160, 175);
      pdf.text('Sin eventos registrados en el timeline.', 24, finalY);
      finalY += 12;
    }

    filteredTimeline.forEach((evento) => {
      finalY = checkPageBreak(finalY, 'Alertas, RRSS y Timeline');

      pdf.setDrawColor(210, 215, 220);
      pdf.line(30, finalY + 3, 30, finalY + 18);
      pdf.setFillColor(9, 147, 181);
      pdf.circle(30, finalY, 2.8, 'F');
      pdf.setFontSize(9);
      pdf.setTextColor(7, 26, 47);
      pdf.text(String(evento.fecha || '-'), 40, finalY + 1);
      pdf.setFontSize(10);
      pdf.text(String(evento.titulo || 'Evento territorial').slice(0, 65), 40, finalY + 8);
      pdf.setFontSize(8);
      pdf.setTextColor(90, 100, 115);
      pdf.text(`Actor: ${evento.actor || '-'}`, 40, finalY + 13);
      finalY += 24;
    });

    
    // ── ALERTAS COGNITIVAS EN PDF ─────────────────────────
    if (cognitiveAlerts.length > 0) {
      addPageWithHeader('Alertas Cognitivas Territoriales');

      let cogY = 44;

      pdf.setTextColor(7, 26, 47);
      pdf.setFontSize(18);
      pdf.text('Alertas detectadas por motor territorial', 20, cogY);

      pdf.setFontSize(10);
      pdf.setTextColor(90, 100, 115);
      pdf.text('Generadas automaticamente. No requieren registro manual.', 20, cogY + 8);

      cogY += 20;

      cognitiveAlerts.forEach((alerta) => {
        cogY = checkPageBreak(cogY, 'Alertas Cognitivas Territoriales');

        const nivelColor = {
          Crítico: [220, 38, 38],
          Alto: [234, 88, 12],
          Medio: [202, 138, 4],
          Bajo: [100, 116, 139]
        }[alerta.nivel] || [100, 116, 139];

        pdf.setFillColor(248, 250, 252);
        pdf.roundedRect(20, cogY, 170, 22, 3, 3, 'F');
        pdf.setFillColor(...nivelColor);
        pdf.roundedRect(20, cogY, 4, 22, 2, 2, 'F');

        pdf.setFontSize(8);
        pdf.setTextColor(...nivelColor);
        pdf.text(`${alerta.tipo} · ${alerta.nivel}`, 28, cogY + 7);

        pdf.setFontSize(10);
        pdf.setTextColor(7, 26, 47);
        pdf.text(String(alerta.titulo).slice(0, 70), 28, cogY + 14);

        const descLineas = pdf.splitTextToSize(alerta.descripcion || '', 155);
        if (descLineas.length > 0) {
          pdf.setFontSize(8);
          pdf.setTextColor(90, 100, 115);
          pdf.text(descLineas[0].slice(0, 100), 28, cogY + 20);
        }

        cogY += 28;
      });
    }

    addFooter(pdf, currentPageRef.value);
    pdf.save('Reporte_Ejecutivo_VITA.pdf');
  };

  useImperativeHandle(ref, () => ({ exportarPDF }));

  // ── RENDER ────────────────────────────────────────────────
  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.title}>Reporte Ejecutivo Territorial</h1>
        <p style={styles.subtitle}>Generación de informe institucional VITA.</p>
      </div>

      {/* INTELIGENCIA TERRITORIAL */}
      <div style={styles.intelligenceSection}>
        <TerritorialIntelligencePanel
          projectActors={filteredProcessedActors}
          projectAlerts={filteredAlerts}
          projectSignals={filteredSignals}
          projectTimeline={filteredTimeline}
          territorialFilters={territorialFilters}
          activeProject={activeProject}
          projectPrediction={projectPrediction}
        />
      </div>

      {/* INTERPRETACIÓN DEL ANALISTA */}
      <div style={styles.analistaCard}>
        <div style={styles.analistaHeader}>
          <div style={styles.analistaBadge}>ANALISTA</div>
          <div style={styles.analistaTitulo}>Interpretación territorial</div>
          <div style={styles.analistaDesc}>
            Escribe tu lectura, guárdala y cárgala cuando necesites. Se incluye en el PDF al exportar.
          </div>
        </div>

        {/* Informes guardados */}
        {informesGuardados.length > 0 && (
          <div style={styles.informesGuardados}>
            <div style={styles.informesLabel}>INTERPRETACIONES GUARDADAS</div>
            <div style={styles.informesList}>
              {informesGuardados.map((inf) => (
                <div
                  key={inf.id}
                  style={{
                    ...styles.informeChip,
                    ...(informeSeleccionadoId === inf.id ? styles.informeChipActive : {}),
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '10px'
                  }}
                >
                  <button
                    style={styles.informeChipInner}
                    onClick={() => cargarInforme(inf)}
                  >
                    <span style={styles.informeChipTitulo}>{inf.titulo}</span>
                    <span style={styles.informeChipFecha}>
                      {new Date(inf.fecha).toLocaleDateString('es-CL')}
                    </span>
                  </button>
                  <button
                    style={styles.informeChipDelete}
                    onClick={(e) => eliminarInforme(e, inf.id)}
                    title="Eliminar interpretación"
                  >
                    x
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <textarea
          style={styles.analistaTextarea}
          placeholder="Escribe tu lectura del territorio: qué está pasando realmente, qué no muestran los datos, qué recomiendas y por qué..."
          value={interpretacionAnalista}
          onChange={(e) => {
            setInterpretacionAnalista(e.target.value);
            setInformeSeleccionadoId(null);
          }}
          rows={6}
        />

        <div style={styles.analistaActions}>
          <input
            style={styles.tituloInput}
            placeholder="Título del informe (opcional)"
            value={tituloInforme}
            onChange={(e) => setTituloInforme(e.target.value)}
          />
          <button
            style={{
              ...styles.guardarBtn,
              opacity: !interpretacionAnalista.trim() || guardando ? 0.5 : 1
            }}
            onClick={guardarInforme}
            disabled={!interpretacionAnalista.trim() || guardando}
          >
            {guardando ? 'Guardando...' : 'Guardar interpretación'}
          </button>
        </div>

        <div style={styles.analistaFoot}>
          {interpretacionAnalista.length > 0
            ? `${interpretacionAnalista.length} caracteres · Se incluirá en el PDF al exportar`
            : 'Sin interpretación — escribe tu lectura y guárdala'}
        </div>
      </div>

      {/* CARD EXPORTACIÓN */}
      <div style={styles.card}>
        <div style={styles.topHeader}>
          <div style={styles.logoBox}>
            <img src={vitaIcon} alt="VITA" style={styles.logo} />
            <div>
              <h2 style={styles.vita}>VITA</h2>
              <p style={styles.tagline}>{brand.tagline}</p>
            </div>
          </div>
          <button style={styles.button} onClick={exportarPDF}>
            Exportar PDF Ejecutivo
          </button>
        </div>

        <h2 style={styles.reportTitle}>
          {activeProject?.nombre || 'Proyecto sin nombre'}
        </h2>

        <p style={styles.text}>
          El informe consolidará estado territorial, narrativas, interpretación del analista, actores críticos con IVT, radar predictivo, mapa y timeline.
        </p>

        <div style={styles.kpiGrid}>
          <MetricCard
            title="Stakeholders"
            value={filteredActors.length}
            description="Actores territoriales en el territorio filtrado."
            icon={<FiUsers />}
          />
          <MetricCard
            title="Stakeholders críticos"
            value={actoresCriticos.length}
            description="Actores con mayor sensibilidad territorial."
            icon={<FiActivity />}
            variant="danger"
          />
          <MetricCard
            title="Alertas activas"
            value={alertasActivas.length}
            description="Eventos que requieren seguimiento."
            icon={<FiAlertTriangle />}
            variant="warning"
          />
          <MetricCard
            title="Pulso territorial"
            value={filteredSignals.length}
            description="Señales sociales detectadas."
            icon={<FiRadio />}
            variant="success"
          />
        </div>

        <div ref={mapRef} style={styles.mapCapture}>
          <MapSection
            pdfMode
            projectActors={filteredActors}
            projectAlerts={filteredAlerts}
            projectSignals={filteredSignals}
            projectTimeline={filteredTimeline}
            territorialFilters={territorialFilters}
            mode="view"
          />
        </div>

        <div
          ref={graphRef}
          style={{
            width: '100%',
            background: '#071a2f',
            borderRadius: '18px',
            overflow: 'hidden',
            marginTop: '20px'
          }}
        >
          <KnowledgeGraph processedActors={filteredProcessedActors} />
        </div>
      </div>
    </div>
  );
});

function normalize(value) {
  return String(value || '').trim().toLowerCase();
}

function itemMatchesTerritory(item, filters) {
  const { selectedRegion, selectedComuna, selectedSector, selectedFoco } = filters;

  if (selectedRegion && normalize(item.region) !== normalize(selectedRegion)) return false;
  if (selectedComuna && normalize(item.comuna) !== normalize(selectedComuna)) return false;
  if (selectedSector && normalize(item.sector) !== normalize(selectedSector)) return false;

  if (selectedFoco) {
    const foco = normalize(item.foco);
    const tags = String(item.tags || '').split(',').map((tag) => normalize(tag));
    if (foco !== normalize(selectedFoco) && !tags.includes(normalize(selectedFoco))) return false;
  }

  return true;
}

function itemMatchesTerritoryFlexible(item, filters, filteredActors) {
  if (itemMatchesTerritory(item, filters)) return true;
  const actorName = normalize(item.actor);
  if (!actorName) return false;
  return filteredActors.some((actor) => normalize(actor.nombre) === actorName);
}

const styles = {
  header: { marginBottom: '20px' },
  title: { margin: 0, fontSize: '34px', color: '#ffffff' },
  subtitle: { color: 'rgba(255,255,255,0.55)', marginTop: '6px' },
  intelligenceSection: { marginBottom: '24px' },
  analistaCard: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: '20px',
    padding: '24px',
    marginBottom: '24px'
  },
  analistaHeader: { marginBottom: '16px' },
  analistaBadge: {
    display: 'inline-block',
    background: 'rgba(31,169,122,0.15)',
    color: '#4ade80',
    border: '1px solid rgba(74,222,128,0.25)',
    padding: '4px 12px',
    borderRadius: '999px',
    fontSize: '11px',
    fontWeight: '900',
    letterSpacing: '1px',
    marginBottom: '10px'
  },
  analistaTitulo: {
    color: 'white',
    fontSize: '20px',
    fontWeight: '700',
    marginBottom: '6px'
  },
  analistaDesc: {
    color: 'rgba(255,255,255,0.50)',
    fontSize: '13px',
    lineHeight: '1.5'
  },
  analistaTextarea: {
    width: '100%',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '14px',
    color: 'white',
    fontSize: '14px',
    lineHeight: '1.7',
    padding: '16px',
    resize: 'vertical',
    fontFamily: 'Arial, sans-serif',
    boxSizing: 'border-box',
    outline: 'none'
  },
  analistaFoot: {
    color: 'rgba(255,255,255,0.35)',
    fontSize: '12px',
    marginTop: '8px'
  },
  card: {
    background: 'white',
    padding: '34px',
    borderRadius: '20px',
    boxShadow: '0 12px 35px rgba(0,0,0,0.08)'
  },
  topHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    gap: '20px'
  },
  logoBox: { display: 'flex', alignItems: 'center', gap: '14px' },
  logo: { width: '64px', height: '64px', objectFit: 'contain' },
  vita: { margin: 0, fontSize: '36px', color: '#071a2f' },
  tagline: { margin: 0, color: '#667085' },
  reportTitle: { fontSize: '26px', color: '#071a2f', marginBottom: '10px' },
  text: { color: '#667085', lineHeight: '1.6' },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))',
    gap: '18px',
    margin: '28px 0'
  },
  mapCapture: { marginTop: '25px', marginBottom: '25px' },
  button: {
    background: 'linear-gradient(135deg,#1fa97a,#0993b5)',
    color: 'white',
    border: 'none',
    padding: '14px 24px',
    borderRadius: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
    whiteSpace: 'nowrap'
  },
  informesGuardados: {
    marginBottom: '16px'
  },
  informesLabel: {
    color: 'rgba(255,255,255,0.40)',
    fontSize: '10px',
    fontWeight: '900',
    letterSpacing: '1px',
    marginBottom: '10px'
  },
  informesList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: '4px'
  },
  informeChip: {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: '10px',
    padding: '8px 14px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '2px',
    textAlign: 'left'
  },
  informeChipActive: {
    background: 'rgba(31,169,122,0.15)',
    border: '1px solid rgba(74,222,128,0.30)'
  },
  informeChipTitulo: {
    color: 'white',
    fontSize: '13px',
    fontWeight: '700'
  },
  informeChipFecha: {
    color: 'rgba(255,255,255,0.40)',
    fontSize: '11px'
  },
  analistaActions: {
    display: 'flex',
    gap: '12px',
    marginTop: '12px',
    marginBottom: '8px'
  },
  tituloInput: {
    flex: 1,
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '10px',
    color: 'white',
    fontSize: '14px',
    padding: '10px 14px',
    outline: 'none',
    fontFamily: 'Arial, sans-serif'
  },
  guardarBtn: {
    background: 'linear-gradient(135deg,#1fa97a,#0993b5)',
    color: 'white',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '10px',
    fontWeight: '700',
    cursor: 'pointer',
    fontSize: '14px',
    whiteSpace: 'nowrap'
  },
  informeChipInner: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '2px',
    flex: 1,
    padding: 0
  },
  informeChipDelete: {
    background: 'rgba(220,38,38,0.12)',
    border: '1px solid rgba(220,38,38,0.25)',
    color: '#ef4444',
    borderRadius: '6px',
    width: '22px',
    height: '22px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '900',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    padding: 0
  },

};

export default ExecutiveReport;